export enum HttpCookieNames {
  CsrfToken = 'X-CSRF-Token',
  SessionId = 'session_id',
  DeviceToken = 'device_token',
  /**
   * First-party vanilla-cookieconsent decision cookie for local and production.
   * Staging and development hosts use environment-suffixed names from
   * `getCookieConsentCookieName` so a production `.starthub.academy` cookie is
   * not consumed on QA hosts.
   */
  CookieConsent = 'starthub_cookie_consent',
  /**
   * Playwright-only cookie that suppresses cookie-consent UI in local E2E.
   * Ignored on production hosts / production builds. Value is '1' when active.
   */
  TestHidePopups = 'starthub_test_hide_popups',
}

export enum AuthAudience {
  Web = 'web',
  Admin = 'admin',
  Partner = 'partner',
}

export interface AuthCookieNames {
  deviceToken: string;
  csrfToken: string;
  csrfSecret: string;
}

const APP_SCOPED_AUTH_COOKIE_NAMES: Record<AuthAudience, AuthCookieNames> = {
  [AuthAudience.Web]: {
    deviceToken: 'starthub_v2_web_device_token',
    csrfToken: 'starthub_v2_web_csrf_token',
    csrfSecret: 'starthub_v2_web_csrf_secret',
  },
  [AuthAudience.Admin]: {
    deviceToken: 'starthub_v2_admin_device_token',
    csrfToken: 'starthub_v2_admin_csrf_token',
    csrfSecret: 'starthub_v2_admin_csrf_secret',
  },
  [AuthAudience.Partner]: {
    deviceToken: 'starthub_v2_partner_device_token',
    csrfToken: 'starthub_v2_partner_csrf_token',
    csrfSecret: 'starthub_v2_partner_csrf_secret',
  },
};

const HOST_COOKIE_PREFIX = '__Host-';

/**
 * Applies the `__Host-` cookie name prefix for secure cookie contexts.
 *
 * Per RFC 6265bis, a `__Host-`-prefixed cookie is only accepted by browsers
 * when it also carries `Secure`, `Path=/`, and no `Domain` attribute. This
 * helper only controls the cookie *name* -- every call site that passes
 * `useSecurePrefix: true` must already set those three cookie options (see
 * apps/backend/src/main.ts, csrf.middleware.ts, and
 * auth-cookie.application-service.ts, which all set `secure`/`path: '/'`
 * without a `domain` whenever they pass a matching secure-context boolean).
 *
 * Local/non-HTTPS environments must pass `false`: browsers silently reject
 * `__Host-` cookies set (or read back) without HTTPS.
 */
export function applyHostCookiePrefix(name: string, useSecurePrefix: boolean): string {
  return useSecurePrefix ? `${HOST_COOKIE_PREFIX}${name}` : name;
}

/**
 * Minimal ambient shape this package needs from a DOM `window` global.
 * This package builds without the "dom" lib (see tsconfig.build.json), so
 * the real `Window`/`Location` types aren't available here -- this narrow
 * local shape avoids depending on them just to read one property.
 */
interface BrowserWindowLike {
  location?: { protocol?: string };
}

/**
 * Browser-only helper that reports whether the current page is served over
 * HTTPS -- i.e. whether the backend is expected to have used `__Host-`
 * prefixed cookie names for this origin.
 *
 * This must resolve to the same boolean the backend used when it set the
 * cookie, in every deployment tier. True local dev (no reverse proxy, plain
 * HTTP) and true production (HTTPS) agree trivially. The EC2 "dev" and
 * "staging" tiers are also served over HTTPS via nginx TLS termination even
 * though the backend there runs with `NODE_ENV=development` -- reading
 * `window.location.protocol` gets this right without needing a build-time
 * environment variable, because it reflects what the browser actually saw,
 * not the backend's application runtime mode.
 *
 * Mirrors the backend's `isSecureCookieDeploymentContext()`
 * (apps/backend/src/config/env.ts), which derives the same boolean from
 * `DEPLOYMENT_ENVIRONMENT` (not `NODE_ENV`) for exactly this reason -- both
 * sides must agree or the frontend will look for a cookie name the backend
 * never set, breaking CSRF validation and login.
 *
 * Returns `false` outside a browser (no `window`). Backend code must derive
 * its own secure-context boolean from server config instead of using this.
 */
export function isSecureCookieBrowserContext(): boolean {
  const globalWithWindow = globalThis as { window?: BrowserWindowLike };
  return globalWithWindow.window?.location?.protocol === 'https:';
}

export function getAuthCookieNames(audience: AuthAudience, useSecurePrefix: boolean): AuthCookieNames {
  const baseNames = APP_SCOPED_AUTH_COOKIE_NAMES[audience];
  return {
    ...baseNames,
    csrfToken: applyHostCookiePrefix(baseNames.csrfToken, useSecurePrefix),
    csrfSecret: applyHostCookiePrefix(baseNames.csrfSecret, useSecurePrefix),
  };
}

export function getSessionCookieName(audience: AuthAudience, useSecurePrefix: boolean): string {
  return applyHostCookiePrefix(`starthub_v2_${audience}_${HttpCookieNames.SessionId}`, useSecurePrefix);
}

export function isLocalAuthCookieHost(hostname: string): boolean {
  const bracketedIpv6 = /^\[([^\]]+)\](?::\d+)?$/.exec(hostname);
  const normalizedHostname =
    bracketedIpv6?.[1] ?? (hostname === '::1' ? hostname : (hostname.split(':')[0] ?? hostname));

  return (
    normalizedHostname === 'localhost' ||
    normalizedHostname.endsWith('.localhost') ||
    normalizedHostname === '127.0.0.1' ||
    normalizedHostname === '::1' ||
    normalizedHostname === 'host.docker.internal'
  );
}

export function parseAuthAudience(value: string | undefined): AuthAudience | undefined {
  return Object.values(AuthAudience).find(audience => audience === value);
}

/**
 * Normalizes a configured app URL to a bare origin, or `undefined` when the
 * value is absent or unparseable.
 *
 * Exported so consumers that need to build a cross-app link — Admin pointing at
 * a web route, for instance — reuse this normalization instead of repeating it.
 */
export function originFromConfiguredUrl(value: string | undefined): string | undefined {
  if (typeof value !== 'string' || value.length === 0) {
    return undefined;
  }
  try {
    return new URL(value).origin;
  } catch {
    return undefined;
  }
}

/**
 * The web application's configured origin, or `undefined` when none is set.
 *
 * `FRONTEND_URL` is already read in this module to resolve the auth audience,
 * so the env read and the normalization both live here rather than being
 * duplicated in an app. Admin uses it to open routes the web app owns, such as
 * a protected download's permanent `/downloads/:slug` link.
 *
 * Read directly, for the reason `getAuthAudienceForConfiguredLocalOrigin`
 * documents below: Vite `define` inlines this identifier into the browser
 * bundle, and a `typeof process === 'undefined'` guard short-circuits before
 * the inlined value is ever reached.
 */
export function getWebAppOrigin(): string | undefined {
  return originFromConfiguredUrl(process.env.FRONTEND_URL);
}

function isSameLocalConfiguredOrigin(origin: string, configured: string | undefined): boolean {
  const configuredOrigin = originFromConfiguredUrl(configured);
  if (configuredOrigin === undefined) {
    return false;
  }
  if (origin === configuredOrigin) {
    return true;
  }
  try {
    const candidate = new URL(origin);
    const expected = new URL(configuredOrigin);
    return (
      isLocalAuthCookieHost(candidate.hostname) &&
      isLocalAuthCookieHost(expected.hostname) &&
      candidate.protocol === expected.protocol &&
      candidate.port === expected.port
    );
  } catch {
    return false;
  }
}

function getAuthAudienceForConfiguredLocalOrigin(origin: string): AuthAudience | undefined {
  // Vite `define` inlines these identifiers into the browser bundle. Do not guard
  // with `typeof process === 'undefined'` — that short-circuits before the inlined
  // origin is used, so host.docker.internal + dynamic e2e-target ports cannot map.
  const adminUrl = process.env.ADMIN_URL;
  const partnerUrl = process.env.PARTNER_URL;
  const frontendUrl = process.env.FRONTEND_URL;
  const landingUrl = process.env.LANDING_URL;

  if (isSameLocalConfiguredOrigin(origin, adminUrl)) {
    return AuthAudience.Admin;
  }
  if (isSameLocalConfiguredOrigin(origin, partnerUrl)) {
    return AuthAudience.Partner;
  }
  if (isSameLocalConfiguredOrigin(origin, frontendUrl) || isSameLocalConfiguredOrigin(origin, landingUrl)) {
    return AuthAudience.Web;
  }
  return undefined;
}

export function getAuthAudienceForOrigin(origin: string | undefined): AuthAudience | undefined {
  if (!origin) return undefined;

  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return undefined;
  }

  const isLocalHost = isLocalAuthCookieHost(url.hostname);
  if (isLocalHost) {
    const envAudience = getAuthAudienceForConfiguredLocalOrigin(url.origin);
    if (envAudience) {
      return envAudience;
    }
    if (url.port === '6001') return AuthAudience.Admin;
    if (url.port === '6002') return AuthAudience.Partner;
    if (url.port === '4000' || url.port === '5173') return AuthAudience.Web;
  }

  const adminHosts = new Set([
    'admin.starthub.academy',
    'admin.dev.starthub.academy',
    'admin.staging.starthub.academy',
  ]);
  if (adminHosts.has(url.hostname)) return AuthAudience.Admin;

  const partnerHosts = new Set([
    'partner.starthub.academy',
    'partner.dev.starthub.academy',
    'partner.staging.starthub.academy',
  ]);
  if (partnerHosts.has(url.hostname)) return AuthAudience.Partner;

  const webHosts = new Set([
    'starthub.academy',
    'www.starthub.academy',
    'founders.starthub.academy',
    'dev.starthub.academy',
    'www.dev.starthub.academy',
    'founders.dev.starthub.academy',
    'staging.starthub.academy',
    'www.staging.starthub.academy',
    'founders.staging.starthub.academy',
  ]);
  return webHosts.has(url.hostname) ? AuthAudience.Web : undefined;
}

export function getAuthAudienceForHost(host: string | undefined): AuthAudience | undefined {
  if (!host) return undefined;
  const firstHost = host.split(',')[0]?.trim();
  if (!firstHost) return undefined;
  return getAuthAudienceForOrigin(`http://${firstHost}`);
}
